<template>
    <div class="venta-filters">
        <div class="filter-bar form-compact">
            <v-text-field :value="search" @input="$emit('update:search', $event)" prepend-inner-icon="fas fa-search"
                label="Buscar por folio de ticket" filled outlined hide-details clearable
                class="filter-bar__grow" @keydown.enter="$emit('search')" @click:clear="$emit('search')">
            </v-text-field>
            <v-autocomplete :value="clientId" @change="$emit('update:clientId', $event || null)"
                :items="clients" item-text="nombreComercial" item-value="idCliente" label="Cliente"
                prepend-inner-icon="fas fa-user" filled outlined hide-details clearable>
            </v-autocomplete>
            <DateRangeField :start="start" :end="end" title="Fecha"
                @update:start="$emit('update:start', $event)" @update:end="$emit('update:end', $event)" />
            <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                    <v-badge :content="advancedCount" :value="advancedCount > 0" color="primary" overlap bordered>
                        <v-btn icon v-bind="attrs" v-on="on" aria-label="Más filtros" @click="$emit('open-advanced')">
                            <v-icon dense>fas fa-sliders-h</v-icon>
                        </v-btn>
                    </v-badge>
                </template>
                <span>Más filtros</span>
            </v-tooltip>
        </div>
        <FilterChips :chips="chips" :total="total" @remove="removeChip" @clear="clearAll">
            <template v-if="$slots.tools" v-slot:tools><slot name="tools"></slot></template>
        </FilterChips>
    </div>
</template>
<script>
import DateRangeField from '@/components/shared/DateRangeField.vue';
import FilterChips from '@/components/shared/FilterChips.vue';
import { describeRange } from '@/utils/dateRange';
import { emptyAdvanced, advancedChips, removeAdvanced } from './ventaFilters';

/**
 * Barra de filtros de la lista de ventas: búsqueda por folio, cliente, rango
 * de fechas (un solo campo con atajos), botón de filtros avanzados y chips con
 * lo que está activo. Las herramientas de la tabla entran por el slot `tools`.
 *
 * No consulta nada: solo edita los valores que le pasa `ListaVenta` con
 * `.sync`. La vista los manda al backend vía `tableFilters` y el mixin
 * resetea la página.
 *
 * Los filtros del panel lateral (`advanced`) no se editan aquí: solo se
 * muestran como chips y se quitan de uno en uno. El panel es
 * `VentaFilterDrawer`.
 */
// Chips de la barra; el resto de las claves son del panel lateral.
const BASE_CHIPS = ['search', 'range', 'client'];

export default {
    name: 'VentaFilters',
    components: { DateRangeField, FilterChips },
    props: {
        search: { type: String, default: '' },
        start: { type: String, default: '' },
        end: { type: String, default: '' },
        clientId: { type: Number, default: null },
        clients: { type: Array, default: () => [] },
        total: { type: Number, default: null },
        advanced: { type: Object, default: emptyAdvanced },
        // Cuántos filtros del panel lateral hay en uso (insignia del botón).
        advancedCount: { type: Number, default: 0 },
        // { clientes, sucursales, tipoSalida }: para poner nombres en los chips.
        catalogs: { type: Object, default: () => ({}) }
    },
    computed: {
        chips() {
            const chips = [];
            if (this.search) chips.push({ key: 'search', label: `Folio: ${this.search}`, icon: 'fas fa-search' });
            if (this.start || this.end) {
                chips.push({ key: 'range', label: `Fecha: ${describeRange(this.start, this.end)}`, icon: 'fas fa-calendar-alt' });
            }
            if (this.clientId) {
                const client = this.clients.find(c => c.idCliente === this.clientId);
                chips.push({ key: 'client', label: `Cliente: ${client ? client.nombreComercial : this.clientId}`, icon: 'fas fa-user' });
            }
            return chips.concat(advancedChips(this.advanced, this.catalogs));
        }
    },
    methods: {
        removeChip(key) {
            if (key === 'search') {
                this.$emit('update:search', '');
                this.$emit('search');
            }
            if (key === 'range') {
                this.$emit('update:start', '');
                this.$emit('update:end', '');
            }
            if (key === 'client') this.$emit('update:clientId', null);
            if (!BASE_CHIPS.includes(key)) this.$emit('update:advanced', removeAdvanced(this.advanced, key));
        },
        clearAll() {
            BASE_CHIPS.forEach(this.removeChip);
            this.$emit('update:advanced', emptyAdvanced());
        }
    }
}
</script>
